import React, { useEffect } from 'react';
import { NavHeader } from '../PageElements/NavHeader';
import Header from '../PageElements/Header';
import Footer from '../PageElements/Footer';
import ScrollReset from '../Tools/ScrollReseter';
import '../CSS/STEM.css';
import HorizontalLine from '../PageElements/HorizontalLine';

export default function STEM() {
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <ScrollReset />
      <div className='wrapper'>
        <NavHeader maxScroll={200} windowWidth={windowWidth} />
        <Header
          imageURL={'Images/STEM.png'}
          title={'S. T. E. M.'}
          titleSize={windowWidth > 750 ? 52 : windowWidth > 492 ? 28 : 16}
          subTitleSize={windowWidth > 750 ? 16 : windowWidth > 492 ? 12 : 10}
          subTitle={'SCIENCE.  TECHNOLOGY.  ENGINEERING.  MATHEMATICS.'}
        />
        <Content windowWidth={windowWidth} />
        <Footer imageURL={'Images/STEM.png'} windowWidth={windowWidth} />
      </div>
    </>
  );
}

function Content({windowWidth}) {
  return (
    <>
      <div className='content' style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
        <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
          <div style={{ width: '950px', gap: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
            <h1 style={{ fontSize: '24px' }}>Empowering the Future Through STEM</h1>

            <p>
              At Sókn Engineering, we believe in the power of science, technology, engineering, and mathematics (STEM) to drive meaningful
              advancements in financial markets, data analytics, and artificial intelligence. Our cutting-edge technology leverages deterministic
              mathematics and AI-driven models to reshape the way commodities and financial instruments are traded.
            </p>
          </div>
        </div>

        <HorizontalLine />

        <Section headerText={'Our Commitment to STEM Excellence'} reversedDirection={true} imgageURl={'Images/STEM2.jpg'} windowWidth={windowWidth}>
          <p style={{ lineHeight: '150%', fontFamily: 'Lato' }}>
            We are committed to fostering STEM innovation through:
            <ul>
              <li>
                <b>Advanced AI & Machine Learning</b> – Developing self-learning models that optimize market predictions.
              </li>
              <li>
                <b>Mathematical Precision</b> – Utilizing proprietary Modular Calculus for predictive analytics.
              </li>
              <li>
                <b>Algorithmic Trading</b> – Engineering state-of-the-art AI models that outperform traditional market strategies.
              </li>
              <li>
                <b>Data Science & Big Data Applications</b> – Harnessing real-time financial data for deep market insights.
              </li>
            </ul>
          </p>
        </Section>

        <HorizontalLine />

        <Section headerText={'STEM in Action: Sókn Engineering’s Impact'} imgageURl={'Images/STEM1.jpg'} windowWidth={windowWidth}>
          <p style={{ lineHeight: '150%', fontFamily: 'Lato' }}>
            We are committed to fostering STEM innovation through:
            <ul>
              <li>
                <b>Mathematical Breakthroughs</b> – Our Lagertha platform processes 550 million algorithms, generating 32 trillion automated thought
                processes, enabling precise pricing predictions to the half-penny.
              </li>
              <li>
                <b>Artificial Intelligence</b> – We apply AI/ML models that are resilient to data noise and market fluctuations.
              </li>
              <li>
                <b>Risk Management & Predictive Analytics</b> – Our technology minimizes loss exposure while maximizing return on investment (ROI)
                through deterministic AI models.
              </li>
            </ul>
          </p>
        </Section>

        <HorizontalLine />

        <Section headerText={'Future STEM Initiatives'} reversedDirection={true} imgageURl={'Images/STEM3.png'} windowWidth={windowWidth}>
          <p style={{ lineHeight: '150%', fontFamily: 'Lato' }}>
            Sókn Engineering is dedicated to advancing STEM fields by:
            <ul>
              <li>
                <b>Developing Next-Generation Predictive Models</b> – Expanding AI and machine learning applications.
              </li>
              <li>
                <b>Collaborating with Universities & Researchers</b> – Partnering with academia to push the boundaries of mathematical modeling.
              </li>
              <li>
                <b>STEM Education & Outreach</b> – Supporting initiatives that inspire the next generation of data scientists, engineers, and AI
                specialists.
              </li>
            </ul>
          </p>
        </Section>

        <HorizontalLine />

        <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'row' }}>
          <div
            style={{
              width: '950px',
              gap: '8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              marginBottom: windowWidth > 750 ? '40px' : '0px',
            }}>
            <h1 style={{ fontSize: '24px' }}>Join Us in Driving STEM Forward</h1>

            <p>
              At Sokn Engineering, STEM isn’t just a concept—it’s the foundation of everything we do. Whether through research, technology, or
              industry partnerships, we are shaping the future of data-driven decision-making and predictive analytics.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function Section({ windowWidth, headerText, imgageURl, reversedDirection, children }) {
  return (
    <>
      {windowWidth > 880 ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '80px', marginTop: '20px', marginBottom: '20px' }}>
            {reversedDirection ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
                  <svg width='2' height='121' viewBox='0 0 2 121' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M1 0.652344V120.189' stroke='url(#paint0_linear_208_222)' />
                    <defs>
                      <linearGradient id='paint0_linear_208_222' x1='1' y1='0.727335' x2='26.5072' y2='6.20883' gradientUnits='userSpaceOnUse'>
                        <stop stopColor='#BC9C4E' />
                      </linearGradient>
                    </defs>
                  </svg>

                  <img
                    alt=''
                    src={imgageURl}
                    height={300}
                    width={420}
                    style={{
                      borderRadius: '10px',
                      border: '1px solid #bc9c4e',
                      boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
                      minWidth: '100px',
                      maxWidth: '420px',
                      height: 'auto',
                      width: '100%',
                    }}
                  />

                  <svg width='2' height='121' viewBox='0 0 2 121' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M1 0.652344V120.189' stroke='url(#paint0_linear_208_222)' />
                    <defs>
                      <linearGradient id='paint0_linear_208_222' x1='1' y1='0.727335' x2='26.5072' y2='6.20883' gradientUnits='userSpaceOnUse'>
                        <stop stopColor='#BC9C4E' />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                <div
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', width: '650px', gap: '10px' }}>
                  <h1 className='bodyHeader'>{headerText}</h1>

                  {children}
                </div>
              </>
            ) : (
              <>
                <div
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', width: '650px', gap: '10px' }}>
                  <h1 className='bodyHeader'>{headerText}</h1>

                  {children}
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
                  <svg width='2' height='121' viewBox='0 0 2 121' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M1 0.652344V120.189' stroke='url(#paint0_linear_208_222)' />
                    <defs>
                      <linearGradient id='paint0_linear_208_222' x1='1' y1='0.727335' x2='26.5072' y2='6.20883' gradientUnits='userSpaceOnUse'>
                        <stop stopColor='#BC9C4E' />
                      </linearGradient>
                    </defs>
                  </svg>

                  <img
                    alt=''
                    src={imgageURl}
                    height={300}
                    width={420}
                    style={{
                      borderRadius: '10px',
                      border: '1px solid #bc9c4e',
                      boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
                      minWidth: '100px',
                      maxWidth: '420px',
                      height: 'auto',
                      width: '100%',
                    }}
                  />

                  <svg width='2' height='121' viewBox='0 0 2 121' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M1 0.652344V120.189' stroke='url(#paint0_linear_208_222)' />
                    <defs>
                      <linearGradient id='paint0_linear_208_222' x1='1' y1='0.727335' x2='26.5072' y2='6.20883' gradientUnits='userSpaceOnUse'>
                        <stop stopColor='#BC9C4E' />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              gap: '20px',
              marginTop: '20px',
              marginBottom: '20px',
              width: '100%',
            }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', width: '100%' }}>
              <svg width='2' height='121' viewBox='0 0 2 121' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M1 0.652344V120.189' stroke='url(#paint0_linear_208_222)' />
                <defs>
                  <linearGradient id='paint0_linear_208_222' x1='1' y1='0.727335' x2='26.5072' y2='6.20883' gradientUnits='userSpaceOnUse'>
                    <stop stopColor='#BC9C4E' />
                  </linearGradient>
                </defs>
              </svg>

              <img
                alt=''
                src={imgageURl}
                height={300}
                width={420}
                style={{
                  borderRadius: '10px',
                  border: '1px solid #bc9c4e',
                  boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)',
                  minWidth: '200px',
                  maxWidth: '420px',
                  height: 'auto',
                  width: '100%',
                }}
              />

              <svg width='2' height='121' viewBox='0 0 2 121' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path d='M1 0.652344V120.189' stroke='url(#paint0_linear_208_222)' />
                <defs>
                  <linearGradient id='paint0_linear_208_222' x1='1' y1='0.727335' x2='26.5072' y2='6.20883' gradientUnits='userSpaceOnUse'>
                    <stop stopColor='#BC9C4E' />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', width: '100%', gap: '10px' }}>
              <h1 className='bodyHeader'>{headerText}</h1>

              {children}
            </div>
          </div>
        </>
      )}
    </>
  );
}